import DataBin from "../../valueObjects/DataBin.js";

class DataBinning {
    /**
     * Creates instance of one dimensional data binning tool
     * @param {number} binSize - size of the bin
     * @param {number} min - optional, min value (inclusive) from which binning will be performed, leave undefined to determine automatically from data during determineBins call
     * @param {number} max - optional, max value (inclusive) to which binning will be performed, leave undefined to determine automatically from data during determineBins call
     * @returns {DataBinning} DataBinning instance
     */
    constructor(binSize, min, max){
        if (typeof(binSize) === 'undefined') {
            throw new TypeError('binSize must be defined');
        }

        if (typeof(binSize) !== 'number') {
            throw new TypeError('binSize must be numeric');
        }

        if (binSize <= 0) {
            throw new Error('binSize must be grater than 0');
        }

        if(typeof(min) !== 'number' && typeof(min) !== 'undefined') {
            throw new TypeError('min must be numeric or undefined');
        }

        if(typeof(max) !== 'number' && typeof(max) !== 'undefined') {
            throw new TypeError('min must be numeric or undefined');
        }

        this.binSize = binSize;

        this.min = min;
        this.max = max;
    }

    /**
     * Simple data binning based one dimensional array of values
     * @param {Array} data array of numeric values
     * @returns {Array} array of bins, each bin will contain size, offset and set of values which were classified to given bin
     */
    determineBins(data) {
        if (!(data instanceof Array)) {
            throw new TypeError('data must be iterable');
        }

        if (!data.length) {
            return [];
        }

        if (data.some(x => typeof(x) !== 'number')) {
            throw new TypeError('All values in data arrays must be numeric');
        }

        let min = this.min;
        let max = this.max;

        if (typeof(min) === 'undefined' && data.length) {
            // a little bit faster https://medium.com/coding-at-dawn/the-fastest-way-to-find-minimum-and-maximum-values-in-an-array-in-javascript-2511115f8621
            min = Math.min.apply(null, data); //Math.min(...data);
        }
        else if(!data.length) {
            return [];
        }

        if (typeof(max) === 'undefined' && data.length) {
            // a little bit faster https://medium.com/coding-at-dawn/the-fastest-way-to-find-minimum-and-maximum-values-in-an-array-in-javascript-2511115f8621
            max = Math.max.apply(null, data); //Math.max(...data);
            if (Number.isFinite(max)) {
                // shift it a bit as bin is defined as [min,max), without it we could skip this point or struggle with calculations at later point
                max += Number.EPSILON;
            }
            /*             
             if (Number.isFinite(max)) {
                const isNegative = max < 0;
                max = (Math.floor(Math.abs(max) / this.binSize) + 1) * this.binSize;
                if (isNegative) {
                    max *= -1;
                }
            } */
        }
        else if(!data.length) {
            return [];
        }

        return this.performBinning(data, this.binSize, min, max);
    }

    performBinning(data, binSize, min, max) {
        const binsSet = this.createSetOfBins(binSize, min, max);

        for (let i = 0; i < data.length; i++) {
            const value = data[i];
            const binNr = this.determineBinNr(value, min, max);

            if (binNr === false || binNr === null) {
                continue;
            }

            if (!binsSet[binNr]) {
                throw new Error(`Unable to categorize value ${i}, unable to find row ${binNr} in set of bins`);
            }

            binsSet[binNr].add(value);
        }

        return binsSet;
    }

    determineBinNr(value, min, max) {
        return Math.floor((value - min) / this.binSize);
    }

    createBin(binSize, offset) {
        return new DataBin(binSize, offset);
    }

    /**
     * Creates array of bins.
     * @param {number} binSize - size of the individual bin
     * @param {number} min - min value (inclusive) from which binning will be performed
     * @param {number} max - max value (inclusive) to which binning will be performed
     * @returns {Array} array containing bins
     */
    createSetOfBins(binSize, min, max) {
        const range = Math.abs(max - min);

        const nrOfBins = Math.ceil(range / binSize);// + 1;

        const bins = [];

        for (let i = 0; i < nrOfBins; i++) {
            bins.push(
                this.createBin(
                    binSize,
                    min + i * binSize,
                )
            );
        }

        return bins;
    }
}

export default DataBinning;